import React, { FC, useEffect, useState } from "react";
import CustomAccordion from "../../accordion/CustomAccordion";
import Loader from "../../common/Loader";
import { Table } from "react-bootstrap";
import MButton from "../../mButton/MButton";
import IEmbassyDetails from "../../../types/IEmbassyDetails";
import EmbassyService from "../../../services/EmbassyService";
import axios from "axios";
import { appInsights } from "../../../appInsights";
import IPracticeRoles from "../../../types/IPracticeRoles";
import RemoveUserConfirmationModal from "../myPractice/components/RemoveUserConfirmationModal";
import AccountService from "../../../services/AccountService";

interface EmbassyMember {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  status: boolean;
}

interface EmbassyMembersResponse {
  data: EmbassyMember[];
}

const EmbassyMemberInfo: FC<{
  embassyInfo: IEmbassyDetails | undefined;
  accessToken: string | undefined;
  userRole: IPracticeRoles | undefined;
}> = ({ embassyInfo, accessToken, userRole }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [embassyMembers, setEmbassyMembers] = useState<EmbassyMember[]>([]);
  const [error, setError] = useState<any>(null);
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
  const [userToRemove, setUserToRemove] = useState<EmbassyMember>();
  const [isSubmitting, setIsSubmitting] = useState<{
    isSubmitting: boolean;
    userId: string;
  }>({ isSubmitting: false, userId: "" });

  const cancelToken = axios.CancelToken.source();

  const checkRole = (): boolean => {
    if (userRole && userRole.embassyRoles) {
      return userRole.embassyRoles.some((e) => e.roleId === "11");
    }
    return false;
  };

  const getEmbassyMembers = (embassyId: number) => {
    setLoading(true);
    EmbassyService.getEmbassyMembers(embassyId, accessToken, cancelToken)
      .then((response: EmbassyMembersResponse) => {
        if (response?.data) {
          setEmbassyMembers(response.data);
        }
        setLoading(false);
      })
      .catch((e) => {
        if (!axios.isCancel(e)) {
          console.error(e);
          appInsights.trackException({ error: e });
          setError(e);
        }
        setLoading(false);
      });
  };

  const removeEmbassyMember = (
    embassyId: number | undefined,
    userId: string | undefined
  ) => {
    if (embassyId && userId) {
      setIsSubmitting({ isSubmitting: true, userId });
      AccountService.removeEmbassyMember(
        { embassyId, userId },
        accessToken,
        cancelToken
      )
        .then(() => {
          setIsSubmitting({ isSubmitting: false, userId });
          getEmbassyMembers(embassyId); // Refetch after removal
        })
        .catch((e) => {
          setIsSubmitting({ isSubmitting: false, userId });
          setError(e);
          if (!axios.isCancel(e)) {
            console.error(e);
            appInsights.trackException({ error: e });
          }
        });
    }
  };

  useEffect(() => {
    if (embassyInfo && accessToken && checkRole()) {
      getEmbassyMembers(embassyInfo.embassyID);
    }
    return () => {
      cancelToken.cancel();
    };
  }, [embassyInfo, accessToken]);
  if (checkRole()) {
    return (
      <>
        <CustomAccordion
          title={
            <div className="header-top d-flex">
              <h5 className="header-blue bold m-0 pe-2">Embassy Members</h5>
              {loading ? (
                <Loader />
              ) : (
                <h5 className="header-blue bold m-0">
                  ({embassyMembers.length})
                </h5>
              )}
            </div>
          }
          content={
            <>
              <p>
                You can manage the list of Embassy members below. If you would
                like to add a new member, please contact the Center for Global
                Health at{" "}
                <a
                  href="mailto:CFGH@chla.usc.edu"
                  className="text-decoration-none"
                >
                  CFGH@chla.usc.edu
                </a>
              </p>

              <div className="table-responsive-sm">
                {loading ? (
                  <></>
                ) : embassyMembers.length > 0 ? (
                  <Table id="lab-table">
                    <thead>
                      <tr>
                        <th>Name</th>
                        <th>Email</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {embassyMembers.map((member) => (
                        <tr key={member.userId}>
                          <td>{member.firstName + " " + member.lastName}</td>
                          <td>{member.email}</td>
                          <td>{member.status ? "Active" : "Inactive"}</td>
                          {userRole && checkRole() && member.status ? (
                            <td className="d-md-flex justify-content-end">
                              <MButton
                                onClick={() => {
                                  setShowRemoveModal(true);
                                  setUserToRemove(member);
                                }}
                                title="Remove"
                                small
                                variant="accent"
                                bgColor="var(--color-purple)"
                                width="85px"
                                isSubmitting={
                                  isSubmitting.isSubmitting &&
                                  isSubmitting.userId === member.userId
                                }
                              />
                            </td>
                          ) : (
                            <td>
                              <div style={{ width: "", height: "37px" }}></div>
                            </td>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                ) : (
                  <p>No embassy members found.</p>
                )}
              </div>
            </>
          }
          isOpen={true}
        />
        <RemoveUserConfirmationModal
          show={showRemoveModal}
          handleClose={() => {
            setShowRemoveModal(false);
            setUserToRemove(undefined);
          }}
          handleConfirm={() => {
            setShowRemoveModal(false);
            removeEmbassyMember(embassyInfo?.embassyID, userToRemove?.userId);
          }}
          isPractice={false}
          name={userToRemove?.firstName + " " + userToRemove?.lastName}
        />
      </>
    );
  }else{
    return <></>
  }
};

export default EmbassyMemberInfo;
